import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import api from '../services/api';

const usePageTracking = () => {
  const location = useLocation();
  const { user } = useAuthStore();
  const viewIdRef = useRef(null);
  const startTimeRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // 페이지 제목 가져오기
  const getPageTitle = () => {
    const titles = {
      '/': '홈',
      '/projects': '프로젝트 목록',
      '/profile': '프로필',
      '/vip': 'VIP',
      '/help': '도움말',
      '/notices': '공지사항',
      '/notifications': '알림',
      '/contractors': '소장 목록',
      '/admin': '관리자 대시보드',
      '/admin/projects': '프로젝트 관리',
      '/admin/contractors': '소장 관리',
      '/admin/customers': '고객 관리',
      '/admin/vip': 'VIP 관리',
      '/admin/notices': '공지사항 관리',
      '/admin/helps': '도움말 관리',
      '/admin/activity': '활동 기록',
      '/admin/analytics': '접속 분석',
      '/admin/notifications': '알림 설정',
      '/admin/settings': '시스템 설정'
    };

    // 동적 URL 처리
    const path = location.pathname;
    if (path.startsWith('/projects/') || path.startsWith('/p/')) return '프로젝트 상세';
    if (path.startsWith('/s/')) return '채팅';
    if (path.startsWith('/contractors/')) return '소장 프로필';
    if (path.startsWith('/vip/')) return 'VIP 상세';
    
    return titles[path] || document.title || path;
  };

  // 페이지 진입 기록
  const recordPageView = async () => {
    if (!user) return;

    try {
      const response = await api.post('/analytics/page-view', {
        page_url: location.pathname,
        page_title: getPageTitle()
      });
      
      if (response.data.view_id) {
        viewIdRef.current = response.data.view_id;
        startTimeRef.current = Date.now();
      }
    } catch (error) {
      console.error('Failed to record page view:', error);
    }
  };

  // 페이지 이탈 기록
  const recordPageLeave = async () => {
    if (!viewIdRef.current || !startTimeRef.current) return;

    const duration = Math.round((Date.now() - startTimeRef.current) / 1000);
    
    try {
      await api.post('/analytics/page-leave', {
        view_id: viewIdRef.current,
        duration_seconds: duration
      });
    } catch (error) {
      console.error('Failed to record page leave:', error);
    }

    viewIdRef.current = null;
    startTimeRef.current = null;
  };

  // 사용자 활동 감지 (마우스 움직임, 키보드 입력)
  useEffect(() => {
    const updateActivity = () => {
      lastActivityRef.current = Date.now();
    };

    window.addEventListener('mousemove', updateActivity);
    window.addEventListener('keypress', updateActivity);
    window.addEventListener('click', updateActivity);
    window.addEventListener('scroll', updateActivity);

    return () => {
      window.removeEventListener('mousemove', updateActivity);
      window.removeEventListener('keypress', updateActivity);
      window.removeEventListener('click', updateActivity);
      window.removeEventListener('scroll', updateActivity);
    };
  }, []);

  // 페이지 변경 감지
  useEffect(() => {
    if (user) {
      // 이전 페이지 이탈 기록
      recordPageLeave();
      
      // 새 페이지 진입 기록
      recordPageView();
    }

    // cleanup: 컴포넌트 언마운트 시 이탈 기록
    return () => {
      recordPageLeave();
    };
  }, [location.pathname, user]);

  // 브라우저 탭 전환/닫기 감지
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        recordPageLeave();
      } else if (user) {
        recordPageView();
      }
    };

    const handleBeforeUnload = () => {
      recordPageLeave();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [user]);

  // 비활성 상태 감지 (5분 동안 활동 없으면 이탈 처리)
  useEffect(() => {
    const checkInactivity = setInterval(() => {
      const inactiveTime = Date.now() - lastActivityRef.current;
      if (inactiveTime > 5 * 60 * 1000 && viewIdRef.current) {
        recordPageLeave();
      }
    }, 60000); // 1분마다 체크

    return () => clearInterval(checkInactivity);
  }, []);
};

export default usePageTracking;